import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import DocumentViewer from './DocumentViewer';
import { logDeeplink } from './preview/viewerDebug';
import { decodeAnchorParam } from '../domain/sourceRef';

// Top-level handler for the citation deep-link URL contract:
//
//   ?doc=<id>&chunk=<chunk_id>&page=<n>&t=<seconds>&q=<snippet>&cr=<s>-<e>
//
// Mounted once at the app level so any route can open the viewer.
// Citations from Ask Arc, match-result evidence links, and external
// share links all flow through this single boundary.
//
// SOTA path: `chunk` is the chunk_id of an indexed source. The viewer
// fetches the chunk's stored EvidenceLocation and dispatches the
// highlight (char_range, time_range, bbox, none) — no fuzzy text
// matching when a location is present.
//
// Pinpoint sub-range: `cr=<start>-<end>` carries chunk-relative char
// offsets of the exact cited span. When present, the viewer composes
// them onto the chunk's page anchor and highlights the cited
// sentence/name instead of the whole chunk. Optional — absence keeps
// today's whole-chunk highlight.
//
// Legacy path: `q` (a snippet) and `page` / `t` (initial position) are
// kept for backward compat with chunks indexed before Phase B and for
// non-chunk-backed citations.
export default function DocumentDeepLink() {
  const [searchParams, setSearchParams] = useSearchParams();
  const docId = searchParams.get('doc');
  const chunkId = searchParams.get('chunk');
  const pageRaw = searchParams.get('page');
  const tRaw = searchParams.get('t');
  const query = searchParams.get('q');
  const crRaw = searchParams.get('cr');
  const anchorRaw = searchParams.get('a');

  // Server-resolved anchor — the "resolve once, render rects" fast
  // path. When status=pinpoint the viewer scrolls + highlights from
  // these verified page-text offsets with no chunk fetch and no DOM
  // text search. Memoized: a fresh object identity per render would
  // thrash the viewer's effects.
  const anchor = useMemo(() => decodeAnchorParam(anchorRaw), [anchorRaw]);

  const initialPage = pageRaw ? Math.max(1, parseInt(pageRaw, 10) || 1) : null;
  const startAt = tRaw != null ? Number(tRaw) : null;

  // Parse `cr=<start>-<end>` into a [start, end] chunk-relative range.
  // Anything malformed → null, so the viewer falls back to the
  // whole-chunk highlight (never worse than today).
  const citeRange = (() => {
    if (!crRaw) return null;
    const m = crRaw.match(/^(\d+)-(\d+)$/);
    if (!m) return null;
    const start = parseInt(m[1], 10);
    const end = parseInt(m[2], 10);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
    return [start, end];
  })();

  // Re-key the viewer when the deep-link target changes so internal
  // state resets cleanly instead of carrying over from the previous
  // citation.
  const key = `${docId || ''}|${chunkId || ''}|${pageRaw || ''}|${tRaw || ''}|${query || ''}|${crRaw || ''}|${anchorRaw || ''}`;
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    if (docId) {
      // Citation observability — surfaces the deep-link params at the
      // URL boundary so we can tell SOTA path (chunk set) apart from
      // legacy path (q only) in one row. Now persisted via viewerDebug
      // for CLI / dashboard queries (Phase 1 of Viewer Anchoring Epic).
      logDeeplink({
        documentId: docId,
        chunkId,
        page: pageRaw,
        t: tRaw,
        q: query,
        hasChunkId: !!chunkId,
        hasSnippet: !!query,
        hasCiteRange: !!crRaw,
        hasAnchor: !!anchorRaw,
        anchorStatus: anchor?.status || null,
      });
      setOpenId(docId);
    }
  }, [docId, chunkId, pageRaw, tRaw, query, crRaw, anchorRaw]);

  if (!openId) return null;

  const close = () => {
    setOpenId(null);
    const next = new URLSearchParams(searchParams);
    next.delete('doc');
    next.delete('chunk');
    next.delete('page');
    next.delete('t');
    next.delete('q');
    next.delete('cr');
    next.delete('a');
    setSearchParams(next, { replace: true });
  };

  // "Open the original here" on an excerpt (and the redaction studio's
  // "Edit redaction in original") re-target the deep link rather than
  // stacking a second viewer: swap the document id, carry a start time,
  // and drop the citation params that belonged to the previous target.
  const openOther = (id, opts = null) => {
    if (!id) return;
    const next = new URLSearchParams(searchParams);
    next.set('doc', id);
    for (const k of ['chunk', 'page', 'q', 'cr', 'a']) next.delete(k);
    const t = Number(opts?.startAt);
    if (Number.isFinite(t) && t > 0) next.set('t', String(Math.floor(t)));
    else next.delete('t');
    setSearchParams(next);
  };

  return (
    <DocumentViewer
      key={key}
      documentId={openId}
      onOpenDocument={openOther}
      onClose={close}
      chunkId={chunkId}
      initialPage={initialPage}
      startAt={Number.isFinite(startAt) ? startAt : null}
      query={query}
      citeRange={citeRange}
      anchor={anchor}
    />
  );
}
